import React, { useEffect, useRef, useState } from 'react'
import { intermediatePageArray } from 'components/tables/tableTools'
import AreaChart from './AreaChart2'
import CompassChart from './CompassChart'
import VelocityChart from './VelocityChart'
//import { unitData, cpuData, diskData, memoryData, speed, bearing } from './data2'

import {
  Row,
  Column,
  createColumnHelper,
  Table as ReactTable,
  PaginationState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  OnChangeFn,
  flexRender,
  RowSelectionTableState,
  RowSelectionState
} from '@tanstack/react-table'

declare module '@tanstack/react-table' {
  interface ColumnMeta {
    className?: string
  }
}

const fixDate = (rawDate) => {
  return new Date(rawDate).toISOString().split('T')[0]
}

export default function ZeusChartTable(props) {

  const columns = props.columns;
  const refreshData = props.refreshData
  const cpuData = props.cpuData
  const diskData = props.diskData
  const memoryData = props.memoryData
  const speed = props.speed
  const bearing = props.bearing
  const diskErrors = props.diskErrors
  const showRowDetails = props.showRowDetails
  const setShowRowDetails = props.setShowRowDetails
  const selectedUnit = props.selectedUnit
  const setSelectedUnit = props.setSelectedUnit

  const rerender = React.useReducer(() => ({}), {})[1]

  const rowSelection = props.rowSelection;
  const setRowSelection = props.setRowSelection;
  const data = props.data;
  //const data = unitData;
  const chartData = props.chartData;


  //Table Control
  const [sorting, setSorting] = React.useState<SortingState>([{id: 'Updated', desc: true}])
  const [globalFilter, setGlobalFilter] = React.useState()
  const [columnVisibility, setColumnVisibility] = React.useState({})

  const searchFilter = (e) => {
    setGlobalFilter(e.currentTarget.value);
  }
  const [showColumnSelection, setShowColumnSelection] = React.useState(false)
  const toggleColumnSelection = (e) => {
    setShowColumnSelection(!showColumnSelection);
  }
  const columnSelection = useRef(null)
  const pageSizeSelection = useRef(null)
  const closeSelection = (e) => {
    if (columnSelection.current && showColumnSelection && !columnSelection.current.contains(e.target)) {
      setShowColumnSelection(false);
    }
    if (pageSizeSelection.current && showPageSizeSelection && !pageSizeSelection.current.contains(e.target)) {
      setShowPageSizeSelection(false);
    }
  }
  useEffect(() => {
    document.addEventListener('mousedown', closeSelection)
    return () => {
      document.removeEventListener('mousedown', closeSelection);
    };
  }, [])
  const [isFullscreen, setIsFullscreen] = React.useState(false)
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  }
  const [selectedPageSize, setSelectedPageSize] = React.useState(10)
  const [showPageSizeSelection, setShowPageSizeSelection] = React.useState(false)
  const togglePageSizeSelection = (e) => {
    setShowPageSizeSelection(!showPageSizeSelection);
  }
  useEffect(() => {
    setShowPageSizeSelection(false);
    table.setPageSize(Number(selectedPageSize))
  }, [selectedPageSize])

  //Table Creation
  const table = useReactTable({
    data,
    columns,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      rowSelection: rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true, //enable row selection for all rows
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    //debugTable: true,
  })

  // showRowDetails state now passed from parent

  const toggleShowRowDetails = (row) => {
    const unitId = row.original.unit_id
    let arr = [...showRowDetails]
    const isExpanding = !arr[row.id]
    arr[row.id] = isExpanding
    setShowRowDetails(arr)
    
    // Select unit on map when expanding row
    if (isExpanding) {
      setSelectedUnit(unitId)
    } else {
      console.log(`Table row ${row.id} collapsed - unit: ${unitId}`)
    }
  }

  return (
    <div>
      { chartData && 
      <div className={`bootstrap-table bootstrap4 ${isFullscreen ? 'fullscreen' : ''}`}>
        <div className="fixed-table-toolbar">
          <div className="bs-bars float-left">
            <div id="toolbar" className="d-none">
              <button id="remove" className="btn btn-danger" disabled="">
                <i className="glyphicon glyphicon-remove"></i> <span data-locale="Delete">Delete</span>
              </button>
              <button className="btn btn-success" data-toggle="modal" data-target="#createUnit">
                <span data-locale="Create">Create</span>
              </button>
            </div>
          </div>
          <div className="columns columns-right btn-group float-right">
            {/* 
          <button className="btn btn-secondary" type="button" name="paginationSwitch" aria-label="Pagination Switch" title="Hide/Show pagination"><i className="fa fa-caret-square-down"></i></button>
          <button className="btn btn-secondary" type="button" name="toggle" aria-label="Toggle" title="Toggle"><i className="fa fa-toggle-off"></i></button>
            */}
            <button className="btn btn-secondary" type="button" name="refresh" title="Refresh" onClick={() => refreshData()}><i className="fa fa-sync"></i></button>
            <button className="btn btn-secondary" type="button" name="fullscreen" title="Fullscreen" onClick={toggleFullscreen}><i className={`fa ${!isFullscreen ? 'fa-arrows-alt' : 'fa-arrows-alt'}`}></i></button>

            <div className="keep-open btn-group" title="Columns">
              <button className="btn btn-secondary dropdown-toggle" onClick={toggleColumnSelection} type="button" data-toggle="dropdown" aria-label="Columns" title="Columns"><i className="fa fa-th-list"></i><span className="caret"></span></button>
              <div ref={columnSelection} className={`dropdown-menu dropdown-menu-right ${showColumnSelection ? 'show' : ''}`}>
                {table.getAllLeafColumns().map(column => {
                  return (
                    <div key={column.id} className="px-1">
                      <label className="dropdown-item dropdown-item-marker">
                        <input
                          {...{
                            type: 'checkbox',
                            checked: column.getIsVisible(),
                            onChange: column.getToggleVisibilityHandler(),
                          }}
                        />
                        {' '} {column.id}
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="float-right search btn-group">
            <input className="form-control search-input" type="text" placeholder="Search" autoComplete="off" onChange={searchFilter} />
          </div>
        </div>
        <div className="fixed-table-container">
          <div className="fixed-table-body">
            <table className="table table-bordered table-hover">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{ textAlign: 'center' }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder ? null : (
                            <div className={`th-inner sortable both ${header.column.getIsSorted()}`}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </div>
                          )}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  return (
                    <>

                      <tr key={row.id} onClick={() => toggleShowRowDetails(row)} style={{ cursor: 'pointer' }}>
                        {row.getVisibleCells().map(cell => {
                          return (
                            <td key={cell.id} className={cell.column.columnDef.meta?.className}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          )
                        })}
                      </tr>
                      <tr className={` ${showRowDetails[row.id] ? ' ' : 'hidden'} detail-view`}>
                        <td colSpan="6">
                          <div className="info-block">
                            <div className="graphik-wrap ch-5">
                              <h1 data-locale="Real time unit status">Real time unit status</h1>
                              <div className="container-fluid" style={{ padding: 0 + 'px !important' }}>
                                <div className="row">
                                  <div className="col-lg-5">
                                    <div className="card">
                                      <div className="card-header">
                                        <div className="row align-items-center">
                                          <div className="col">
                                            <h3 className="mb-0" data-locale="CPU">CPU</h3>
                                          </div>
                                          <div className="col text-right">

                                          </div>
                                        </div>
                                      </div>
                                      <div className="card-body">
                                      { chartData && row.original.unit_id && cpuData && cpuData[row.original.unit_id] &&
                                        <AreaChart
                                          value={cpuData[row.original.unit_id]}
                                          id={"root-cpu-" + row.id}
                                          div={"chartdiv-cpu-" + row.id}
                                        />
                                      }
                                      </div>
                                    </div>
                                    <div className="card">
                                      <div className="card-header">
                                        <div className="row align-items-center">
                                          <div className="col">
                                            <h3 className="mb-0" data-locale="Memory">Memory</h3>
                                          </div>
                                          <div className="col text-right">
                                          </div>
                                        </div>
                                      </div>
                                      <div className="card-body">
                                      { chartData && row.original.unit_id && memoryData && memoryData[row.original.unit_id] &&
                                        <AreaChart
                                          value={memoryData[row.original.unit_id]}
                                          id={"root-memory-" + row.id}
                                          div={"chartdiv-memory-" + row.id}
                                        />
                                      }
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-lg-5">
                                    <div className="card">
                                      <div className="card-header">
                                        <div className="row align-items-center">
                                          <div className="col">
                                            <h3 className="mb-0" data-locale="System disk">
                                              System disk
                                              {diskErrors && diskErrors[row.original.unit_id] && 
                                                <span style={{color: 'red', fontSize: '0.8em'}}> - Failed to get disk info</span>
                                              }
                                            </h3>
                                          </div>
                                          <div className="col text-right">
                                          </div>
                                        </div>
                                      </div>
                                      <div className="card-body">
                                      { chartData && row.original.unit_id && diskData && diskData[row.original.unit_id] &&
                                        <AreaChart
                                          value={diskData[row.original.unit_id]}
                                          id={"root-systemDisk-" + row.id}
                                          div={"chartdiv-systemDisk-" + row.id}
                                        />
                                      }
                                      </div>
                                    </div>
                                    <div className="card">
                                      <div className="card-header">
                                        <div className="row align-items-center">
                                          <div className="col">
                                            <h3 className="mb-0" data-locale="Storage">Storage</h3>
                                          </div>
                                          <div className="col text-right">
                                          </div>
                                        </div>
                                      </div>
                                      <div className="card-body">
                                      { chartData && row.original.unit_id && diskData && diskData[row.original.unit_id] &&
                                        <AreaChart
                                          value={diskData[row.original.unit_id]}
                                          id={"root-storage-" + row.id}
                                          div={"chartdiv-storage-" + row.id}
                                        />
                                      }
                                      </div>
                                    </div>
                                  </div>

                              <div className="col-lg-2">
                                <div className="card">
                                  <div className="card-header ">
                                    <h5 className="h3 mb-0" data-locale="Speed">Speed</h5>
                                  </div>
                                  <div className="card-body">
                                    <div id="graph-speed-131">
                                    { chartData && row.original.unit_id && speed &&
                                      
                                    <VelocityChart
                                        value={speed[row.original.unit_id]}
                                        id={"root-velocity-" + row.id}
                                        div={"chartdiv-velocity-" + row.id}
                                      />
                                    }
                                    </div>
                                  </div>
                                </div>
                                <div className="card">
                                  <div className="card-header ">
                                    <h5 className="h3 mb-0" data-locale="Bearing">Bearing</h5>
                                  </div>
                                  <div id="compass-131">
                                  { chartData && row.original.unit_id && diskData && diskData[row.original.unit_id] &&
                                      
                                    <CompassChart
                                        value={bearing[row.original.unit_id]}
                                        id={"root-bearing-" + row.id}
                                        div={"chartdiv-bearing-" + row.id}
                                        />
                                  }
                                  </div>
                                  <div className="compass-footer-131"></div>
                                </div>
{/* 
                                <a href="unitDetails/131" className="btn btn-xs btn-success" data-locale="Details">Details</a>
                                 */}
                              </div>
                              </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr >
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div >

        <div className="fixed-table-pagination" >
          <div className="float-left pagination-detail">

            <span className="pagination-info">Showing {selectedPageSize * table.getState().pagination.pageIndex + 1}
              {' '} to {selectedPageSize * table.getState().pagination.pageIndex + table.getRowModel().rows.length}
              {' '} of {table.getFilteredRowModel().rows.length} rows
            </span>
            <div className="page-list">
              <div className="btn-group dropdown dropup">
                <button className="btn btn-secondary dropdown-toggle" type="button" onClick={togglePageSizeSelection}>
                  <span className="page-size">{selectedPageSize}</span>
                  <span className="caret"></span>
                </button>
                <div ref={pageSizeSelection} className={`dropdown-menu ${showPageSizeSelection ? 'show' : ''}`} >
                  {[10, 25, 50].map(pageSize => (
                    <a key={pageSize} className={`dropdown-item ${pageSize == selectedPageSize ? 'active' : ''}`} onClick={() => setSelectedPageSize(pageSize)}>{pageSize}</a>
                  ))}
                </div>
              </div>
              {' '} rows per page
            </div>
          </div>

          <div className="float-right pagination">
            {table.getPageCount() > 1 &&
              <ul className="pagination">
                <li className={`page-item page-pre ${!table.getCanPreviousPage() ? 'disabled' : ''}`}><a className="page-link" onClick={() => table.previousPage()}>‹</a></li>
                <li className={`page-item page-pre ${table.getState().pagination.pageIndex == 0 ? 'active' : ''}`}><a className="page-link" onClick={() => table.setPageIndex(0)}>1</a></li>
                {table.getState().pagination.pageIndex > 3 && table.getPageCount() > 5 &&
                  <li className="page-item page-first-separator disabled"><a className="page-link">...</a></li>
                }
                {intermediatePageArray(table.getState().pagination.pageIndex, table.getPageCount()).map(pageIdx => (
                  <li key={pageIdx} className={`page-item ${pageIdx == (table.getState().pagination.pageIndex) ? 'active' : ''}`}><a className="page-link" onClick={() => table.setPageIndex(pageIdx)}>{pageIdx + 1}</a></li>
                ))}

                {table.getState().pagination.pageIndex < (table.getPageCount() - 4) && table.getPageCount() > 5 &&
                  <li className="page-item page-first-separator disabled"><a className="page-link">...</a></li>
                }
                <li className={`page-item page-pre ${table.getPageCount() == (table.getState().pagination.pageIndex + 1) ? 'active' : ''}`}><a className="page-link" onClick={() => table.setPageIndex(table.getPageCount() - 1)}>{table.getPageCount()}</a></li>
                <li className={`page-item page-next ${!table.getCanNextPage() ? 'disabled' : ''}`}><a className="page-link" onClick={() => table.nextPage()}>›</a></li>
              </ul>
            }
          </div>
        </div>
      </div >
  }
    </div >
  )
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')
